/**
 * RepeatsDetailTemplate - Shared template for the Recurring Series
 * detail view (works for both Work Order and Inspection series).
 *
 * Atomic Design role: Template. Composes:
 *   - A header band with series name + schedule summary + status badge
 *   - Series-level action buttons: Cancel series, Delete series
 *   - The occurrences table with per-row reschedule / cancel / delete
 *     actions
 *
 * The per-type detail page (RecurringSeriesDetailPage) is responsible
 * for loading the series, deciding the `dateColumn` to read off each
 * occurrence, and providing the mutation callbacks. This template
 * keeps the layout/markup consistent so a future series-type can be
 * added without touching the visual contract.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../atoms/Spinner';
import { Button, BackLink } from '../../atoms';
import { RecurrenceIcon } from '../../atoms/RecurrenceIcon';
import { formatRecurrenceSummary, formatRecurrenceEnd, type RecurrenceRule } from '../../../utils/recurrence';
import { formatDay } from '../../../utils/formatDate';
import type { RecurrenceSeriesRecord, SeriesOccurrence } from '../../../services/recurrenceSeriesService';

/* ============================================
 * TYPES
 * ============================================ */

export interface RepeatsDetailTemplateProps {
	/** The loaded series. Pass null while loading; the template renders the spinner. */
	series: RecurrenceSeriesRecord | null;
	/** True while fetching the series. */
	isLoading: boolean;
	/** Error message, or null when no error. */
	error: string | null;
	/** Back-link target (the matching list page). */
	backTo: string;
	/** Route to the underlying record's detail page (WO or Inspection detail). */
	occurrenceRoute: (occurrenceId: number) => string;
	/** Column on each occurrence carrying the scheduled date. */
	dateColumn: 'scheduled_date' | 'inspection_date';
	/** Series-level cancel handler — confirms server-side. */
	onCancelSeries: () => void | Promise<void>;
	/** Per-occurrence reschedule handler — receives the occurrence + a new ISO date. */
	onRescheduleOccurrence: (occurrence: SeriesOccurrence, newDate: string) => void | Promise<void>;
	/** Per-occurrence cancel handler — receives the occurrence + optional reason. */
	onCancelOccurrence: (occurrence: SeriesOccurrence, reason?: string) => void | Promise<void>;
	/** Per-occurrence delete handler — silent hide. */
	onDeleteOccurrence: (occurrence: SeriesOccurrence) => void | Promise<void>;
	/** Optional extra classes merged onto the root element */
	className?: string;
	/**
	 * Overrides the root's data-ref marker. Per the atomic-design
	 * convention, a page that composes this template as its root passes
	 * its OWN identity here, so the rendered DOM reflects the real
	 * component rather than the shared wrapper.
	 */
	dataRef?: string;
}

/* ============================================
 * STATUS PALETTE — per-occurrence */
const OCCURRENCE_STATE_COLORS: Record<string, string> = {
	scheduled: 'bg-blue-50 text-blue-700 border border-blue-200',
	cancelled: 'bg-orange-50 text-orange-700 border border-orange-200',
	deleted: 'bg-gray-100 text-gray-500 border border-gray-300',
};

const SERIES_STATUS_COLORS: Record<string, string> = {
	active: 'bg-blue-100 text-blue-700 border border-blue-300',
	paused: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
	ended: 'bg-green-100 text-green-700 border border-green-300',
	cancelled: 'bg-gray-200 text-gray-500 border border-gray-300',
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const RepeatsDetailTemplate: React.FC<RepeatsDetailTemplateProps> = ({
	series,
	isLoading,
	error,
	backTo,
	occurrenceRoute,
	dateColumn,
	onCancelSeries,
	onRescheduleOccurrence,
	onCancelOccurrence,
	onDeleteOccurrence,
	className,
	dataRef = 'template-repeats-detail-template',
}) => {
	const navigate = useNavigate();

	if (isLoading) {
		return (
			<div className="p-24px flex items-center justify-center min-h-[400px]">
				<Spinner size="lg" label="Loading recurring schedule" />
			</div>
		);
	}

	if (error || !series) {
		return (
			<div className="p-24px">
				<div className="bg-red-50 border border-red-200 rounded-lg p-24px text-center">
					<p className="text-14px font-sans text-red-700">{error || 'Series not found'}</p>
					<button type="button" onClick={() => navigate(backTo)} className="mt-12px text-14px font-sans font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">
						Back
					</button>
				</div>
			</div>
		);
	}

	// Rebuild the rule object so the shared formatter can render the
	// summary + end fragment.
	const rule: RecurrenceRule = {
		frequency_unit: series.frequency_unit,
		interval: series.interval,
		by_weekday: series.by_weekday ?? [],
		start_date: series.start_date,
		end_type: series.end_type,
		end_date: series.end_date,
		end_count: series.end_count,
	};

	const occurrences: SeriesOccurrence[] = (
		series.type === 'work_order' ? (series.work_orders ?? []) : (series.inspections ?? [])
	).slice().sort((a, b) => {
		const da = (a[dateColumn] || '') as string;
		const db = (b[dateColumn] || '') as string;
		return da.localeCompare(db);
	});

	return (
		<div className={['repeats-detail-template', 'p-16px tablet:p-24px font-sans', className].filter(Boolean).join(' ')} data-ref={dataRef}>
			<BackLink to={backTo} />

			{/* --- Header --- */}
			<div className="repeats-detail-template__header flex flex-col tablet:flex-row tablet:items-start tablet:justify-between gap-12px mb-24px">
				<div>
					<div className="flex items-center gap-12px flex-wrap">
						<h1 className="page-title text-24px font-heading text-brand-heading m-0 flex items-center gap-8px">
							<RecurrenceIcon className="w-20px h-20px text-brand-primary" />
							{series.name}
						</h1>
						<span className={['inline-flex items-center px-8px py-2px rounded-full text-12px font-semibold uppercase tracking-wide', SERIES_STATUS_COLORS[series.status] || SERIES_STATUS_COLORS.active].join(' ')}>
							{series.status}
						</span>
					</div>
					<p className="text-14px text-brand-text-muted mt-4px max-w-[640px]">
						{formatRecurrenceSummary(rule)} {formatRecurrenceEnd(rule) || '· no end date'}
					</p>
					{/* Jobs are created on their date, not up front — say when the
					    next one will appear so an empty list below isn't a puzzle. */}
					{series.next_occurrence_date && (
						<p className="repeats-detail-template__next text-13px text-brand-text mt-4px">
							Next job is created on <span className="font-semibold">{formatDay(series.next_occurrence_date)}</span>.
						</p>
					)}
				</div>
				<div className="flex items-center gap-8px">
					<Button variant="ghost" onClick={() => onCancelSeries()}>Cancel Series</Button>
				</div>
			</div>

			{/* --- Occurrences table --- */}
			<h2 className="text-16px font-semibold text-brand-text mb-12px">Occurrences ({occurrences.length})</h2>
			{occurrences.length === 0 ? (
				<div className="px-16px py-32px text-center text-14px text-brand-text-muted border border-dashed border-brand-border rounded-md bg-white">
					No occurrences generated yet.
				</div>
			) : (
				<div className="repeats-detail-template__table overflow-x-auto border border-brand-border rounded-md bg-white">
					<table className="w-full min-w-[640px] text-14px">
						<thead className="bg-brand-surface text-brand-text-muted text-12px uppercase tracking-wide">
							<tr>
								<th className="text-left px-16px py-12px font-medium">Record</th>
								<th className="text-left px-16px py-12px font-medium">Date</th>
								<th className="text-left px-16px py-12px font-medium">State</th>
								<th className="text-left px-16px py-12px font-medium">Status</th>
								<th className="text-left px-16px py-12px font-medium">Actions</th>
							</tr>
						</thead>
						<tbody>
							{occurrences.map((occ) => {
								const dateValue = (occ[dateColumn] || '') as string;
								const isHidden = occ.occurrence_state !== 'scheduled';
								return (
									<tr key={occ.id} className={['border-t border-brand-border hover:bg-brand-surface/40', isHidden ? 'opacity-70' : ''].join(' ')}>
										<td className="px-16px py-12px">
											<button type="button" onClick={() => navigate(occurrenceRoute(occ.id))} className="text-14px font-semibold text-brand-accent hover:underline text-left">
												{occ.project_name ?? `#${occ.id}`}
											</button>
										</td>
										<td className="px-16px py-12px">
											{isHidden ? (
												<span className="text-12px text-brand-text-muted">{formatDay(dateValue)}</span>
											) : (
												<input
													type="date"
													value={dateValue ? String(dateValue).split('T')[0] : ''}
													onChange={(e) => {
														if (e.target.value && e.target.value !== dateValue) {
															onRescheduleOccurrence(occ, e.target.value);
														}
													}}
													className="px-8px py-8px border border-brand-border rounded-md text-16px desktop:text-13px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary"
												/>
											)}
										</td>
										<td className="px-16px py-12px">
											<span className={['inline-flex items-center px-8px py-2px rounded-full text-11px font-semibold uppercase tracking-wide', OCCURRENCE_STATE_COLORS[occ.occurrence_state] || OCCURRENCE_STATE_COLORS.scheduled].join(' ')}>
												{occ.occurrence_state}
											</span>
											{occ.cancelled_reason && (
												<div className="text-11px text-brand-text-muted mt-2px">{occ.cancelled_reason}</div>
											)}
										</td>
										<td className="px-16px py-12px text-12px text-brand-text-muted">{occ.status}</td>
										<td className="px-16px py-12px">
											{isHidden ? (
												<span className="text-12px text-brand-text-muted">—</span>
											) : (
												<div className="flex items-center gap-12px">
													<button
														type="button"
														onClick={() => {
															const reason = window.prompt('Reason for cancelling (optional):') || undefined;
															onCancelOccurrence(occ, reason);
														}}
														className="text-12px font-semibold text-orange-700 hover:underline min-h-44px inline-flex items-center px-8px"
													>
														Cancel
													</button>
													<button
														type="button"
														onClick={() => {
															if (window.confirm('Delete this occurrence? It will be hidden from the main list and removed from this series’s active count.')) {
																onDeleteOccurrence(occ);
															}
														}}
														className="text-12px font-semibold text-red-600 hover:underline min-h-44px inline-flex items-center px-8px"
													>
														Delete
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default RepeatsDetailTemplate;
